import { memo, useMemo } from "react";
import { Input, Button, Card, Space, Row, Col, Tag, Typography } from "antd";
import { DeleteOutlined, SettingOutlined } from "@ant-design/icons";
import { titleCase, internationalizeNumericValue, parseInternationalizedValue, validateMaxCampaignBudget } from "@/ts/utils";
import { RxReload } from "react-icons/rx";
import {
    BCC_CAMPAIGN_TYPES,
    ICampaignCollectionBreakdownProps,
    ICollection,
} from "@/ts/common-types";
import { MIN_BULK_CAMPAIGN_BUGET_LIMIT } from "@/ts/constants";
import useGetModulesData from "@/hooks/useGetModulesData";

const CampaignCollectionBreakdown = memo(
    ({
        cluster,
        clientCurrencySymbol,
        collectionId,
        handleCampaignUpdate,
        showCombinedView,
        clusterId,
        onAutoSuggestBudget,
    }: ICampaignCollectionBreakdownProps) => {
        const channelOptions = useGetModulesData({ type: "channel-options" });
        const { campaigns, campaignBudget } = useMemo(() => {
            if (showCombinedView) {
                const combinedClusterCampaigns = cluster?.collections
                    ?.map((collection: ICollection) => collection.campaigns)
                    .flat();
                return { campaigns: combinedClusterCampaigns, campaignBudget: 0 };
            }
            const foundCollection = cluster?.collections?.find(
                (collection: any) => collection.id === collectionId
            );
            return {
                campaigns: foundCollection?.campaigns,
                campaignBudget: Number(foundCollection?.budget) || 0
            };
        }, [cluster?.collections, collectionId, showCombinedView]);

        const minCampaignBudget = useMemo(() => {
            return channelOptions.minCampaignBudget || MIN_BULK_CAMPAIGN_BUGET_LIMIT;
        }, [channelOptions.minCampaignBudget]);

        return (
            <Card
                style={{
                    width: "100%",
                    borderRadius: 0
                }}
                styles={{
                    header: {
                        backgroundColor: "#CBD9E7",
                        borderBottom: "none",
                        borderRadius: 0,
                    },
                    body: {
                        backgroundColor: "#F0F4F8",
                        padding: 0,
                        borderRadius: 0,
                    }
                }}
                title={
                    <Space
                        style={{
                            width: "100%",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <Typography.Text strong>
                            Campaign Collection Breakdown
                        </Typography.Text>
                        <Space>
                            <Button
                                type="text"
                                icon={
                                    <SettingOutlined
                                        size={16}
                                        className="text-primary-700"
                                    />
                                }
                            />
                            <Button
                                type="primary"
                                disabled={true}
                                onClick={() => {
                                    if (onAutoSuggestBudget && clusterId) {
                                        onAutoSuggestBudget(clusterId, collectionId);
                                    }
                                }}
                            >
                                Auto-suggest Budget
                            </Button>
                        </Space>
                    </Space>
                }
            >
                <div
                    style={{
                        padding: "16px",
                    }}
                >
                    <Row gutter={[16, 16]}>
                        {(campaigns || []).map(
                            (campaign: any, index: number) => (
                                <Col span={8} key={index}>
                                    <Card
                                        size="small"
                                        styles={{
                                            body: {
                                            height: "128px",
                                            padding: "16px",
                                                borderRadius: "8px",
                                                border: "1px solid #e6f7ff",
                                                backgroundColor:
                                                    campaign?.isArchived
                                                        ? "#f5f5f5"
                                                        : "#ffffff",
                                            }
                                        }}
                                        className={
                                            campaign?.isArchived
                                                ? "archived-card"
                                                : ""
                                        }
                                        variant="outlined"
                                    >
                                        <div style={{ width: "100%" }}>
                                            {/* Top row: Tag and Delete button */}
                                            <div
                                                style={{
                                                    display: "flex",
                                                    justifyContent:
                                                        "space-between",
                                                    alignItems: "center",
                                                    marginBottom: "12px",
                                                }}
                                            >
                                                <Tag
                                                    className="fs-7 fw-700 text-primary-700 bg-light-blue m-0"
                                                    style={{
                                                        border: "1px solid #0078AA",
                                                        borderRadius: "8px",
                                                        padding: "4px 8px",
                                                    }}
                                                >
                                                    {campaign?.cohortLabel || titleCase(
                                                        campaign?.type ===
                                                            BCC_CAMPAIGN_TYPES.MULTIPLE
                                                            ? campaign
                                                                  .keywordType[0]
                                                            : campaign.keywordType
                                                                  ?.map(
                                                                      (
                                                                          ad: string
                                                                      ) =>
                                                                          ad
                                                                              .charAt(
                                                                                  0
                                                                              )
                                                                              ?.toUpperCase()
                                                                  )
                                                                  ?.join("/")
                                                    )}
                                                </Tag>
                                                <Button
                                                    type="text"
                                                    icon={
                                                        campaign?.isArchived ? (
                                                            <RxReload
                                                                size={17}
                                                                className="text-success"
                                                            />
                                                        ) : (
                                                            <DeleteOutlined
                                                                size={17}
                                                                className="text-error"
                                                            />
                                                        )
                                                    }
                                                    size="small"
                                                    style={{
                                                        width: "28px",
                                                        height: "28px",
                                                        borderRadius: "6px",
                                                        border: "1px solid #e5e7eb",
                                                        backgroundColor:
                                                            "#ffffff",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent:
                                                            "center",
                                                        padding: 0,
                                                    }}
                                                    onClick={() =>
                                                        handleCampaignUpdate(
                                                            campaign?.collectionId,
                                                            campaign,
                                                            {
                                                                isArchived:
                                                                    !campaign?.isArchived,
                                                            }
                                                        )
                                                    }
                                                />
                                            </div>

                                            {/* Bottom row: Text and Price input */}
                                            <div
                                                style={{
                                                    display: "flex",
                                                    justifyContent:
                                                        "space-between",
                                                    alignItems: "center",
                                                }}
                                            >
                                                <Typography.Text
                                                    className="text-muted fs-7 fw-400 m-0"
                                                >
                                                    {(() => {
                                                        const cities = campaign?.city || [];
                                                        const isMultiple = campaign?.type === BCC_CAMPAIGN_TYPES.MULTIPLE;
                                                        
                                                        // Check if there's a cohort, region, tier, or zone entry
                                                        let cohortEntry: string | undefined;
                                                        let regionEntry: string | undefined;
                                                        let tierEntry: string | undefined;
                                                        let zoneEntry: string | undefined;
                                                        
                                                        for (const city of cities) {
                                                            if (!city) continue;
                                                            if (!cohortEntry && city.startsWith('cohort:')) {
                                                                cohortEntry = city;
                                                            } else if (!regionEntry && city.startsWith('region:')) {
                                                                regionEntry = city;
                                                            } else if (!tierEntry && city.startsWith('tier:')) {
                                                                tierEntry = city;
                                                            } else if (!zoneEntry && city.startsWith('zone:')) {
                                                                zoneEntry = city;
                                                            }
                                                            // Early exit if all found
                                                            if (cohortEntry && regionEntry && tierEntry && zoneEntry) break;
                                                        }
                                                        
                                                        let cityDisplay = '';
                                                        if (cohortEntry) {
                                                            // Parse cohort format: cohort:level:displayLabel:cityIds
                                                            const parts = cohortEntry.slice(7).split(':');
                                                            if (parts.length >= 2) {
                                                                cityDisplay = parts[1]; // Use the stored display label
                                                            } else {
                                                                // Fallback for legacy format
                                                                const cohortCityIds = parts[0].split('|');
                                                                const firstCityName = cohortCityIds[0]?.split("_")?.[2] || '';
                                                                cityDisplay = cohortCityIds.length > 1 
                                                                    ? `${firstCityName} +${cohortCityIds.length - 1}`
                                                                    : firstCityName;
                                                            }
                                                        } else if (regionEntry) {
                                                            // Parse region format: region:displayLabel:cityIds
                                                            const parts = regionEntry.slice(7).split(':');
                                                            cityDisplay = parts[0] || ''; // Display label
                                                        } else if (tierEntry) {
                                                            // Parse tier format: tier:displayLabel:cityIds
                                                            const parts = tierEntry.slice(5).split(':');
                                                            cityDisplay = parts[0] || ''; // Display label
                                                        } else if (zoneEntry) {
                                                            // Parse zone format: zone:displayLabel:cityIds
                                                            const parts = zoneEntry.slice(5).split(':');
                                                            cityDisplay = parts[0] || ''; // Display label
                                                        } else {
                                                            // Regular city selection (no cohort/region/tier/zone)
                                                            const firstCity = cities[0]?.split("_")?.[2];
                                                            const cityCount = cities.length;
                                                            
                                                            cityDisplay = isMultiple
                                                                ? titleCase(firstCity || "")
                                                                : cityCount > 1
                                                                ? `${firstCity} +${cityCount - 1}`
                                                                : firstCity || "";
                                                        }
                                                        
                                                        const firstTimeSlot = campaign?.timeSlot?.[0];
                                                        const timeSlotCount = campaign?.timeSlot?.length ?? 0;
                                                        
                                                        const timeSlotDisplay = isMultiple
                                                            ? titleCase(firstTimeSlot || "")
                                                            : timeSlotCount > 1
                                                            ? `${titleCase(firstTimeSlot || "")} +${timeSlotCount - 1}`
                                                            : "";
                                                        //!Note[DND]: timeSlots based combination not required now...
                                                        // return cityDisplay && timeSlotDisplay
                                                        //     ? `${cityDisplay} X ${timeSlotDisplay}`
                                                        //     : "";
                                                        return cityDisplay && timeSlotDisplay
                                                            ? `${cityDisplay} X All Day`
                                                            : cityDisplay || "";
                                                    })()}
                                                </Typography.Text>
                                                <Input
                                                    type="text"
                                                    className="fs-7 fw-700"
                                                    prefix={
                                                        clientCurrencySymbol
                                                    }
                                                    value={campaign.budget ? internationalizeNumericValue(campaign.budget) : ''}
                                                    disabled={
                                                        campaign.isArchived
                                                    }
                                                    status={campaign.budget !== null && campaign.budget < minCampaignBudget ? "error" : ""}
                                                    style={{
                                                        width: "110px",
                                                        height: "28px",
                                                        borderRadius: "6px",
                                                        textAlign: "left",
                                                    }}
                                                    onChange={(e) => {
                                                        const numericValue = parseInternationalizedValue(e.target.value);
                                                        if (!validateMaxCampaignBudget(numericValue)) {
                                                            return;
                                                        }
                                                        handleCampaignUpdate(
                                                            collectionId,
                                                            campaign,
                                                            {
                                                                budget: isNaN(numericValue) ? 0 : numericValue,
                                                            }
                                                        );
                                                    }}
                                                />
                                            </div>
                                            {campaign.budget !== null && campaign.budget < minCampaignBudget && (
                                                <div className="d-flex justify-content-end w-100 mt-1">
                                                    <small className="text-danger">Minimum budget is {minCampaignBudget}</small>
                                                </div>
                                            )}
                                        </div>
                                    </Card>
                                </Col>
                            )
                        )}
                    </Row>
                </div>
            </Card>
        );
    }
);

export default CampaignCollectionBreakdown;